import React from "react";
import { FaRegCircleQuestion } from "react-icons/fa6";
import { twMerge } from "tailwind-merge";
import type { LgvCheckboxOption } from "../utils/checkboxOptions";
import {
  isCheckboxOptionSelected,
  toggleCheckboxOption,
} from "../utils/checkboxOptions";

export interface LgvCheckboxGroupProps {
  name: string;
  value: unknown[];
  options: LgvCheckboxOption[];
  onChange: (value: unknown[]) => void;
  disabled?: boolean;
  className?: string;
  onOptionHintClick?: (
    hint: { title?: string; content: string },
    event: React.MouseEvent<HTMLButtonElement>,
  ) => void;
}

const LgvCheckboxGroup: React.FC<LgvCheckboxGroupProps> = ({
  name,
  value,
  options,
  onChange,
  disabled = false,
  className,
  onOptionHintClick,
}) => {
  return (
    <div
      role="group"
      className={twMerge("space-y-2", className)}
    >
      {options.map((opt, index) => {
        const optionId = `${name}-${index}`;
        const isChecked = isCheckboxOptionSelected(value, opt.value);

        return (
          <label
            key={optionId}
            htmlFor={optionId}
            className={twMerge(
              "block cursor-pointer rounded-lg border p-3 transition-colors",
              isChecked
                ? "border-[#1B365D] bg-[#EFF6FF] shadow-[0_0_0_1px_rgba(27,54,93,0.08)]"
                : "border-gray-200 bg-white hover:border-[#1B365D]/35 hover:bg-slate-50",
              disabled && "cursor-not-allowed opacity-50 hover:bg-white hover:border-gray-200",
            )}
          >
            <input
              type="checkbox"
              id={optionId}
              name={name}
              checked={isChecked}
              disabled={disabled}
              onChange={(event) =>
                onChange(
                  toggleCheckboxOption(value, opt.value, event.target.checked),
                )
              }
              className="sr-only"
            />
            <div className="flex items-center gap-2">
              <span
                className={twMerge(
                  "min-w-0 flex-1 text-base text-[#1B365D]",
                  isChecked && "font-medium",
                )}
              >
                {opt.label}
              </span>
              {opt.explanation && onOptionHintClick && (
                <button
                  type="button"
                  className="shrink-0 text-gray-400 hover:text-[#1B365D] transition-colors"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onOptionHintClick(
                      {
                        title: opt.label,
                        content: opt.explanation ?? "",
                      },
                      event,
                    );
                  }}
                  aria-label="Show option hint in sidebar"
                >
                  <FaRegCircleQuestion className="text-sm" />
                </button>
              )}
            </div>
          </label>
        );
      })}
    </div>
  );
};

export default LgvCheckboxGroup;
